/* eslint-disable no-empty-pattern */
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../../config/httpConfig";

// Fetch notifications
export const getNotifications = createAsyncThunk(
  "notification/getNotifications",
  async ({ page = 1, limit = 10 } = {}, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `/api/investor/getNotificationList?page=${page}&limit=${limit}`
      );
      return response.response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Mark notification as read
export const notifyMarkAsRead = createAsyncThunk(
  "notification/notifyMarkAsRead",
  async ({ notificationId, payload }, { rejectWithValue }) => {
    try {
      const response = await axios.put(
        `/api/investor/markAsRead/${notificationId}`,
        payload
      );
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Mark all notification as read
export const notifyMarkAllAsRead = createAsyncThunk(
  "notification/notifyMarkAllAsRead",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.put(`/api/investor/markAllAsRead`);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Mark all notification as read
export const deleteNotifications = createAsyncThunk(
  "notification/deleteNotifications",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.put(`/api/investor/deleteNotifications`, payload);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const NotifySlice = createSlice({
  name: "notification",
  initialState: {
    notifyList: [],
    unreadCount: 0,
    loading: false,
    error: null,
    page: 1,
    totalCount: 0,
    markAllAsReadLoading: false,
    deleteNotificationsLoading: false,
  },

  reducers: {
    // Add new notification from socket
    addNotification: (state, action) => {
      const exists = state.notifyList.some(
        (n) => n.notificationId === action.payload.notificationId
      );
      if (!exists) {
        state.notifyList = [action.payload, ...state.notifyList];
        state.unreadCount += 1;
      }
    },
    markNotificationAsRead: (state, action) => {
      const notificationId = action.payload;
      const notif = state.notifyList.find(
        (n) => n.notificationId === notificationId
      );
      if (notif && !notif.isRead) {
        notif.isRead = true;
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      }
    },
    // Mark all as read (local)
    markAllAsRead: (state) => {
      state.notifyList = state.notifyList.map((n) => ({ ...n, isRead: true }));
      state.unreadCount = 0;
    },
    clearNotifyError: (state) => {
      state.error = null;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(getNotifications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getNotifications.fulfilled, (state, action) => {
        const { notifications, totalCount, unreadCount, page } = action.payload;

        if (page && page > 1) {
          // Append next page of notifications
          state.notifyList = [...state.notifyList, ...notifications];
        } else {
          // Replace on first load
          state.notifyList = notifications;
        }

        state.totalCount = totalCount;
        state.unreadCount = unreadCount;
        state.page = page || 1;
        state.loading = false;
        state.error = null;
      })
      .addCase(getNotifications.rejected, (state, action) => {
        state.loading = false;
        state.notifyList = [];
        state.error = action.payload;
      })
      .addCase(notifyMarkAllAsRead.pending, (state, action) => {
        state.markAllAsReadLoading = true;
        state.error = null;
      })
      .addCase(notifyMarkAllAsRead.fulfilled, (state, action) => {
        state.markAllAsReadLoading = false;
        state.error = null;
      })
      .addCase(notifyMarkAllAsRead.rejected, (state, action) => {
        state.markAllAsReadLoading = false;
        state.error = state.error = action.payload;
      })
      .addCase(deleteNotifications.pending, (state, action) => {
        state.deleteNotificationsLoading = true;
        state.error = null;
      })
      .addCase(deleteNotifications.fulfilled, (state, action) => {
        const deletedIds = action.meta.arg.ids;

        state.deleteNotificationsLoading = false;
        state.error = null;

        // remove deleted notifications
        state.notifyList = state.notifyList.filter(
          (n) => !deletedIds.includes(n.notificationId)
        );

        // update total & unread counts
        state.totalCount = Math.max(0, state.totalCount - deletedIds.length);
        state.unreadCount = state.notifyList.filter((n) => !n.isRead).length;
      })
      .addCase(deleteNotifications.rejected, (state, action) => {
        state.deleteNotificationsLoading = false;
        state.error = action.payload;
      })
  },
});

export const {
  clearNotifyError,
  addNotification,
  markNotificationAsRead,
  markAllAsRead,
} = NotifySlice.actions;

export default NotifySlice.reducer;
